import * as React from "react";
import { Col } from "@lunchboxinc/lunchbox-components";
import { Image, Condition } from "components/elements";
import { Card as CardComps, Tabs, Loader, Footer } from "components/fragments";
import { LocationList } from "components/templates";
import { Text } from "components/elementsThemed";
import { withTemplate } from "components/hocs";
import { images, Copy, Routes, config } from "utils";
import csx from "classnames";
import LocationsMap from "../locationsMap";
import css from "./locationType1.module.scss";
import { Schemas } from "utils";

const { Card, CardBody } = CardComps;

const { Condition: If } = Condition;

const Locations = ({
  clearLocations,
  deliveryChange,
  deliveryFetchCount,
  diningOption,
  displayAddressForm,
  fetching,
  getGeo,
  getPickupGeo,
  googleMapKey,
  handleStreetChange,
  locationCardRefs,
  locations,
  locationState,
  locationTitle,
  onClickMarker,
  onLocationSelected,
  onTabChange,
  style: { labels, cells, segmentViews },
  tabOrder,
  isMarketPlace,
  ...props
}) => {
  const { address, lat, long } = locationState;

  // There are two contexts - Order and Patron.
  // This issue https://lunchboxinc.atlassian.net/browse/ENG-3725 occurs when the patron
  // context has the last address saved and order context doesn't have any address
  // So, the location list is fetched and populated with the patron context address
  // And since the order context doesn't have the address, there's error on checkout.
  // Simple solution here is to not show the locations if there's no address in context
  const isValidDeliveryAddress =
    diningOption && Schemas.AddressSchema.isValidSync(address);

  const { history } = props;

  const locationSection = () => {
    if (fetching) {
      return (
        <div className={css.loader}>
          <Loader />
        </div>
      );
    }

    if (locations.length) {
      if (diningOption === "delivery" && !isValidDeliveryAddress) {
        return <></>;
      }
      return (
        <LocationList
          diningOption={diningOption}
          locations={locations}
          type={cells.primary}
          refs={(id, ref) => {
            const locationCardRefsTemp = { ...locationCardRefs };
            locationCardRefsTemp.current[id] = ref;
          }}
          onClick={onLocationSelected}
        />
      );
    }
    if (diningOption === "delivery" && isValidDeliveryAddress) {
      return (
        <div className={css.nothing}>
          <Text type={labels.secondary}>
            <If is={!deliveryFetchCount.current}>
              <span className={css["static-text"]}>
                {Copy.LOCATIONS_STATIC.SEARCH_DELIVER_LOCATIONS_MESSAGE}
              </span>
              <If is={images?.art_search}>
                <Image
                  className={css["nothing--image"]}
                  src={images?.art_search}
                  alt=""
                />
              </If>
            </If>
            <div aria-live="polite">
              <If is={deliveryFetchCount.current}>
                <span className={css["static-text"]}>
                  {Copy.LOCATIONS_STATIC.NO_LOCATIONS_TO_DELIVER_MESSAGE}
                </span>
                <Image
                  className={css["nothing--image"]}
                  src={images?.art_empty_locations}
                  alt=""
                />
                <span className={css["static-text"]}>
                  {Copy.LOCATIONS_STATIC.PICK_UP_SUGGESTION_MESSAGE}
                </span>
              </If>
            </div>
          </Text>
        </div>
      );
    }

    return <></>;
  };

  const locationContainerClass = csx(
    css["location-container"],
    config.theme.location.footer === "sticky" &&
      css["location-container-padded"],
  );

  return (
    <div className={css.location}>
      <Col className={locationContainerClass} xs="1" sm="1-2" lg="1-3">
        <Card
          className={css["location-container--header"]}
          tabIndex={undefined}
          role={undefined}
        >
          <CardBody bordered={false} className={css.body}>
            <Text
              role="heading"
              aria-level="1"
              aria-label={locationTitle}
              className={css["body-title"]}
              type={labels.primary}
            >
              {locationTitle}
            </Text>
            {images?.art_misc && (
              <Image
                src={images?.art_misc}
                className={`misc-art ${css["body-art"]}`}
                mediaName="art_misc"
                mediaType="svg"
                alt=""
              />
            )}
          </CardBody>
          <CardBody className={css.tabs}>
            <Tabs
              destroyInactiveTabPane
              type={segmentViews.standard}
              activeKey={diningOption}
              onTabChange={onTabChange}
            >
              {tabOrder.map((option) => (
                <div
                  key={option.diningOption}
                  title={option.name}
                  data-cy={`tab-${diningOption}-content`}
                >
                  <Col xs="1" className={css["tabs-container"]}>
                    {displayAddressForm &&
                      option.form({
                        address,
                        clearLocations,
                        deliveryChange,
                        getGeo,
                        getPickupGeo,
                        handleStreetChange,
                      })}
                  </Col>
                </div>
              ))}
            </Tabs>
          </CardBody>
        </Card>
        <ul
          className={css["location-container--cards"]}
          data-cy="location-list"
        >
          {locationSection()}
        </ul>
        <Footer
          page="location"
          className={css["location-container--footer"]}
          type={cells.footer}
          style={{ maxWidth: "100%" }}
        />
      </Col>
      <Col
        className={css["location-map"]}
        xs={{ hidden: true, span: "1" }}
        sm="1-2"
        lg="2-3"
      >
        <LocationsMap
          type={cells.secondary}
          locations={locations}
          center={{ lat, lng: long }}
          googleMapKey={googleMapKey}
          onClickMarker={onClickMarker}
          googleMapURL={Routes.GOOGLE_MAP(googleMapKey)}
          loadingElement={<div style={{ height: "100%" }} />}
          containerElement={<div style={{ height: "100%" }} />}
          mapElement={<div style={{ height: "100%" }} />}
          history={history}
          diningOption={diningOption}
          isMarketPlace={isMarketPlace}
        />
      </Col>
    </div>
  );
};

export default withTemplate(Locations, "location");
